import React, {useMemo} from 'react';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
} from '@chakra-ui/react';
import {
  AiFieldDefinition,
  CrmFieldDefinition,
  RoleCountFieldDefinition,
} from '../../../shared/fields/enrichment';
import {useCustomer, useIsFeatureEnabled} from '../../../hooks/api/metadata';
import {pluralize} from '../../../lib/helpers';
import {ChevronDown} from '@carbon/icons-react';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {CrmFieldModal} from './CrmFieldModal';
import {IntentFieldModal} from './IntentFieldModal';
import {useConnectedCrmIntegration} from '../../../hooks/api/crm';
import {useDisclosure} from '@chakra-ui/react';
import {getFieldLabel} from '../../../shared/fields/types';
import {IntentFieldDefinition} from '../../../shared/fields/intent';
import {AiFieldsTable} from './AiFieldsTable';
import {SimpleEnrichmentFieldsTable} from './SimpleEnrichmentFieldsTable';
import {IntentFieldsTable} from './IntentFieldsTable';

export const Fields = () => {
  const customer = useCustomer();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const integration = useConnectedCrmIntegration();
  const crmFieldModal = useDisclosure();
  const intentFieldModal = useDisclosure();
  const hasIntent = useIsFeatureEnabled('intent');

  // Separate fields by type
  const {aiFields, crmFields, roleCountFields, intentFields, totalCount} =
    useMemo(() => {
      const fieldDefinitions = customer.fieldDefinitions ?? [];
      const ai: AiFieldDefinition[] = [];
      const crm: CrmFieldDefinition[] = [];
      const roleCount: RoleCountFieldDefinition[] = [];
      const intent: IntentFieldDefinition[] = [];

      fieldDefinitions.forEach((field) => {
        if (field.type === 'intent') {
          intent.push(field as IntentFieldDefinition);
        } else if (field.type === 'ai') {
          ai.push(field);
        } else if (field.type === 'crm') {
          crm.push(field as CrmFieldDefinition);
        } else if (field.type === 'roleCount') {
          roleCount.push(field as RoleCountFieldDefinition);
        }
      });

      // Sort AI fields by published status, then by agent, then by label
      ai.sort((a, b) => {
        const publishedCompare =
          a.status === 'published' && b.status !== 'published'
            ? -1
            : a.status !== 'published' && b.status === 'published'
              ? 1
              : 0;
        if (publishedCompare !== 0) {
          return publishedCompare;
        }

        const agentCompare = a.agentId
          .toString()
          .localeCompare(b.agentId.toString());
        if (agentCompare !== 0) {
          return agentCompare;
        }

        return getFieldLabel(a).localeCompare(getFieldLabel(b));
      });

      // Sort other fields by label
      crm.sort((a, b) => getFieldLabel(a).localeCompare(getFieldLabel(b)));
      roleCount.sort((a, b) =>
        getFieldLabel(a).localeCompare(getFieldLabel(b))
      );
      intent.sort((a, b) => getFieldLabel(a).localeCompare(getFieldLabel(b)));

      return {
        aiFields: ai,
        crmFields: crm,
        roleCountFields: roleCount,
        intentFields: intent,
        totalCount: fieldDefinitions.length,
      };
    }, [customer.fieldDefinitions]);

  const tabConfigs = useMemo(() => {
    return [
      {
        label: 'AI',
        count: aiFields.length,
        element: <AiFieldsTable fieldDefinitions={aiFields} />,
      },
      {
        label: 'CRM',
        count: crmFields.length,
        element: <SimpleEnrichmentFieldsTable fieldDefinitions={crmFields} />,
      },
      {
        label: 'Intent',
        count: intentFields.length,
        element: <IntentFieldsTable fieldDefinitions={intentFields} />,
      },
      {
        label: 'Role Count',
        count: roleCountFields.length,
        element: (
          <SimpleEnrichmentFieldsTable fieldDefinitions={roleCountFields} />
        ),
      },
    ].filter(({count}) => count > 0);
  }, [aiFields, crmFields, intentFields, roleCountFields]);

  return (
    <Flex direction="column" gap="4">
      <Flex justifyContent="space-between">
        <Box>
          <Text fontWeight="500">Fields</Text>
          <Text textColor="kgray.300" fontSize="sm">
            {pluralize(totalCount, 'field', 'fields')}
          </Text>
        </Box>
        {isKeyplayAdmin && (
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="kbuttonblue"
              fontWeight="normal"
              rightIcon={<ChevronDown />}
            >
              Create Field
            </MenuButton>
            <MenuList>
              <MenuItem
                isDisabled={!integration}
                onClick={crmFieldModal.onOpen}
              >
                CRM Field
              </MenuItem>
              <MenuItem
                isDisabled={!hasIntent}
                onClick={intentFieldModal.onOpen}
              >
                Intent Field
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>

      {totalCount === 0 ? (
        <Flex direction="column" align="center" p={8}>
          <Text textColor="kgray.300">No custom fields defined.</Text>
        </Flex>
      ) : (
        <Tabs>
          <TabList>
            {tabConfigs.map((config) => (
              <Tab key={config.label}>
                {config.label} ({config.count})
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabConfigs.map((config) => (
              <TabPanel key={config.label} px={0} pt={6}>
                {config.element}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}

      <CrmFieldModal
        isOpen={crmFieldModal.isOpen}
        onClose={crmFieldModal.onClose}
      />
      <IntentFieldModal
        isOpen={intentFieldModal.isOpen}
        onClose={intentFieldModal.onClose}
      />
    </Flex>
  );
};
